import { toDayJs } from '~/services/TimeService';

import {
  ConflictInfo,
  ConflictResolution,
  ExistingBlackoutDate,
  ISODateRange,
  RateConflictInfo,
} from '~/types/stopsell';

export function sliceResolutionsByRoom(
  resolutions: Record<string, ConflictResolution>,
  roomIndex: number,
): Record<string, ConflictResolution> {
  const prefix = `${roomIndex}-`;
  const result: Record<string, ConflictResolution> = {};
  for (const [key, value] of Object.entries(resolutions)) {
    if (key.startsWith(prefix)) {
      result[key.slice(prefix.length)] = value;
    }
  }
  return result;
}

export function detectConflict(newRange: ISODateRange, existing: ExistingBlackoutDate): ConflictInfo | null {
  const ns = toDayJs(newRange.start);
  const ne = toDayJs(newRange.end);
  const es = toDayJs(existing.start);
  const ee = toDayJs(existing.end);

  if (ne.isBefore(es) || ns.isAfter(ee)) {
    return null;
  }

  const existingCoversNew = !es.isAfter(ns) && !ee.isBefore(ne);
  const newCoversExisting = !ns.isAfter(es) && !ne.isBefore(ee);

  if (existingCoversNew) {
    return { type: 'covered_by_existing', existingRange: existing };
  }

  if (newCoversExisting) {
    return { type: 'covers_existing', existingRange: existing };
  }

  return {
    type: 'partial_overlap',
    existingRange: existing,
    mergedRange: {
      start: ns.isBefore(es) ? newRange.start : existing.start,
      end: ne.isAfter(ee) ? newRange.end : existing.end,
    },
  };
}

export function findRateConflict(
  newRange: ISODateRange,
  rateId: string,
  blackoutDates: Array<ExistingBlackoutDate>,
): RateConflictInfo {
  for (const existing of blackoutDates) {
    const existingBlock = existing.blocks.find((b) => b.room_rate_id === rateId);
    if (!existingBlock) {
      continue;
    }

    const conflict = detectConflict(newRange, existing);
    if (!conflict) {
      continue;
    }

    // DB trigger prevents overlapping blocks for the same rate,
    // so at most one conflict can exist here.
    return {
      status: conflict.type,
      existingBlock,
      existingRange: existing,
      mergedRange: conflict.mergedRange,
    };
  }

  return { status: 'clear' };
}
